import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export const NOTIFICATION_KEYS = {
  all: ['notifications'],
  list: (params) => ['notifications', 'list', params],
  unreadCount: () => ['notifications', 'unread-count'],
};

// ─── 알림 목록 ────────────────────────────────────────────────────────────────
export const useNotificationList = (params) =>
  useQuery({
    queryKey: NOTIFICATION_KEYS.list(params),
    queryFn: () =>
      api.get('/notifications', { params }).then((r) => {
        const d = r.data.data;
        return {
          items: Array.isArray(d) ? d : (d?.items || []),
          total: Array.isArray(d) ? d.length : (d?.total || 0),
        };
      }),
    staleTime: 30 * 1000,
  });

// ─── 읽지 않은 알림 개수 (60초 폴링) ──────────────────────────────────────────
// M-4: staleTime 60s 명시 — 포커스 시 불필요한 매번 re-fetch 방지 (NotificationBell 파리티)
export const useNotificationCount = () =>
  useQuery({
    queryKey: NOTIFICATION_KEYS.unreadCount(),
    queryFn: () =>
      api.get('/notifications/unread-count').then((r) =>
        r.data.success ? (r.data.data?.count ?? r.data.data ?? 0) : 0
      ),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

// ─── 알림 읽음 처리 Mutation ──────────────────────────────────────────────────
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  // Phase AE: cross-domain invalidation — Approval Inbox 배지 + Alert 카운트 즉시 갱신
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    queryClient.invalidateQueries({ queryKey: ['approval-inbox-summary'] });
    queryClient.invalidateQueries({ queryKey: ['alerts'] });
  };

  const markOne = useMutation({
    mutationFn: (id) =>
      api.patch(`/notifications/${id}/read`).then((r) => r.data),
    onSuccess: invalidateAll,
  });

  const markAll = useMutation({
    mutationFn: () =>
      api.patch('/notifications/read-all').then((r) => r.data),
    onSuccess: invalidateAll,
  });

  return { markOne, markAll };
};

// ─── 알림 삭제 Mutation ───────────────────────────────────────────────────────
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      api.delete(`/notifications/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['approval-inbox-summary'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
};
